/**
 * The agent load of an analytics server, as `GET /api/v1/status` of the AI Agent Toolbox reports
 * it. On a first start the toolbox installs the pip dependencies of its agents for many minutes.
 */

import { z } from 'zod';

import type { LoadState } from './snapshot';

export const AgentLoadPhase = {
    Loading: 'loading',
    Ready: 'ready',
    Cancelled: 'cancelled',
} as const;
export type AgentLoadPhase = typeof AgentLoadPhase[keyof typeof AgentLoadPhase];

export const AgentLoadStep = {
    Scanning: 'scanning',
    InstallingDependencies: 'installing_dependencies',
    Initializing: 'initializing',
    /** A step of a newer toolbox; the card shows no progress inside the agent for it. */
    Unknown: 'unknown',
} as const;
export type AgentLoadStep = typeof AgentLoadStep[keyof typeof AgentLoadStep];

/** The `agents` object of the status. Without a dependency or a download, its fields are empty. */
export const AgentLoadProgressSchema = z.object({
    phase: z.enum(AgentLoadPhase),
    // A newer toolbox may load its agents in more steps.
    step: z.enum(AgentLoadStep).catch(AgentLoadStep.Unknown),
    total: z.int(),
    loaded: z.int(),
    failed: z.int(),
    /** The agent that loads now; empty between two agents. */
    current: z.string(),
    elapsedMs: z.number(),
    /** The package name only: the status has no sign-in, and a pip spec can hold credentials. */
    dependency: z.string(),
    dependencyIndex: z.int(),
    dependencyCount: z.int(),
    downloadFile: z.string(),
    downloadedBytes: z.number(),
    downloadTotalBytes: z.number(),
});
export type AgentLoadProgress = z.infer<typeof AgentLoadProgressSchema>;

export const ToolboxStatusSchema = z.object({
    agents: AgentLoadProgressSchema,
});

/** The agent load of one analytics server as the store holds it; null until it loads. */
export interface AgentLoadSnapshot extends LoadState {
    progress: AgentLoadProgress | null;
}
